import { FC, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { actionUpdateReportSkuCv } from "../../../../redux/action/userSingleReport/actionUserSingleReport";
import { formatReportData } from "../../../../utils/formatReportData";
import { AppDispatch } from "../../../../redux/reducer/store";

import EditPopup from "../../../custom/editPopup/editPopup";
import Select from "../../../custom/select/select";
import SelectPopup from "../../../custom/selectPopup/selectPopup";
import ArrowIcon from "../../../../assets/icons/chevron.svg";
import { IReportSkuCvOptions } from "../../../../types/reportsInterfaces";

const getSkuCvValueType = (elem: IReportSkuCvOptions) =>
  Object.values(elem.type).join("");

const isSkuCvValueSelectType = (type: string) =>
  type === "Фото" || type === "Да/Нет" || type === "Справочник";

const getSkuCvValueSelectOptions = (elem: IReportSkuCvOptions) => {
  const type = getSkuCvValueType(elem);

  if (type === "Фото") {
    return [
      { value: "Сделано", label: "Сделано" },
      { value: "Не сделано", label: "Не сделано" },
    ];
  }
  if (type === "Да/Нет") {
    return [
      { value: "Да", label: "Да" },
      { value: "Нет", label: "Нет" },
    ];
  }
  if (type === "Справочник") {
    return (elem.dictionaryValues ?? []).map(({ name }) => ({
      value: name,
      label: name,
    }));
  }
  return [];
};

const SkuCvTableSection: FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  const { report } = useSelector(
    (state: any) => state.userSingleReport.userSingleReport
  );
  const { claims } = useSelector((state: any) => state.user.user.data);

  const [openValueIndex, setOpenValueIndex] = useState<number>(-1);
  const [newValue, setNewValue] = useState<string>("");
  const [isVisibleData, setIsVisibleData] = useState<boolean>(true);

  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    document.addEventListener("mousedown", handleDocumentClick);

    return () => {
      document.removeEventListener("mousedown", handleDocumentClick);
    };
  }, []);

  const handleClickCancelRenameBtn = () => {
    setOpenValueIndex(-1);
  };

  const handleChangeReportData = (index: number) => {
    if (index === openValueIndex) {
      return;
    } else {
      setOpenValueIndex(index);
    }
  };

  const handleAcceptRenameSkuCv = (elem: IReportSkuCvOptions) => {
    dispatch(
      actionUpdateReportSkuCv({
        reportId: report && report.reportId.id,
        data: [
          {
            sku: {
              id: elem.sku.id,
              name: elem.sku.name,
            },
            name: elem.name,
            value: newValue || elem.value,
            hotline: elem.hotline,
          },
        ],
      })
    );

    handleClickCancelRenameBtn();
    setNewValue("");
  };

  const handleHotlineSelectChange = (
    selectedOption: { value: string; label: string },
    elem: IReportSkuCvOptions
  ) => {
    dispatch(
      actionUpdateReportSkuCv({
        reportId: report && report.reportId.id,
        data: [
          {
            sku: {
              id: elem.sku.id,
              name: elem.sku.name,
            },
            name: elem.name,
            value: elem.value,
            hotline: selectedOption.value,
          },
        ],
      })
    );
  };

  const handleAcceptEditOnKeyPress = (event: React.KeyboardEvent<HTMLInputElement>, elem: IReportSkuCvOptions) => {
    if (event.key === "Enter") {
      handleAcceptRenameSkuCv(elem);
    }
  };

  const handleDocumentClick = (event: MouseEvent) => {
    const target = event.target as Element;
    if (
      target.closest('[role="listbox"]') ||
      target.closest('[role="option"]')
    ) {
      return;
    }
    if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
      setOpenValueIndex(-1);
    }
  };

  const skuCvList: IReportSkuCvOptions[] = report?.skuCv ?? [];

  const hasAnyWithPlan = skuCvList.some(
    (elem) => elem.category.DisplayWithPlan === "Выкладка (с планом)"
  );

  return (
    <div className={`sectionData ${isVisibleData ? 'sectionData_active sectionData__overflow' : ''}`}>
      <span className="section__title">Доп. данные по товарам</span>
      <button
        className='sectionData_button'
        onClick={() => setIsVisibleData(!isVisibleData)}
      >
        <img src={ArrowIcon} alt="Custom Indicator" className='icon' />
      </button>
      <table className="customerOutlet__data_table">
        <thead className="tunder__thead">
          <tr>
            <th>Наименование</th>
            <th>SKU</th>
            {hasAnyWithPlan && <th>План</th>}
            <th>Значение</th>
            <th>Горячая линия</th>
          </tr>
        </thead>
        <tbody>
          {skuCvList.map((elem: IReportSkuCvOptions, index: number) => {
            const skuCvValueType = getSkuCvValueType(elem);

            return (
              <tr key={index}>
                <td>{elem.name}</td>
                <td>
                  {elem?.brand} {elem.sku.name}
                </td>
                {hasAnyWithPlan && (
                  <td>
                    {elem.category.DisplayWithPlan === "Выкладка (с планом)"
                      ? elem.weightPlan
                      : "—"}
                  </td>
                )}
                <td
                  onClick={() => {
                    if (
                      claims.includes('admin') ||
                      claims.includes('reports:edit')
                    ) {
                      handleChangeReportData(index)
                    }
                  }}
                  className="edit__data_text"
                >
                  <div className={
                      formatReportData(elem?.changes)?.includes("value")
                        ? "changed__table_value"
                        : formatReportData(elem?.equals)?.includes("Value")
                          ? "previous__table_value"
                          : ""
                    }>
                  <span>
                    {elem.value}
                  </span>
                  </div>

                  {openValueIndex === index &&
                    (isSkuCvValueSelectType(skuCvValueType) ? (
                      <SelectPopup
                        handleClickAcceptBtn={() => handleAcceptRenameSkuCv(elem)}
                        handleClickCancelBtn={handleClickCancelRenameBtn}
                        handleChangeValue={(selectedOption) => {
                          setNewValue(selectedOption.value);
                        }}
                        value={newValue}
                        defaultValue={elem.value}
                        options={getSkuCvValueSelectOptions(elem)}
                        modalRef={modalRef}
                      />
                    ) : (
                      <EditPopup
                        handleClickAcceptRenameBtn={() => {
                          handleAcceptRenameSkuCv(elem);
                        }}
                        handleAcceptOnKeyPress={(
                          event: React.KeyboardEvent<HTMLInputElement>
                        ) => handleAcceptEditOnKeyPress(event, elem)}
                        handleClickCancelRenameBtn={handleClickCancelRenameBtn}
                        handleChangeValue={(
                          event: React.ChangeEvent<HTMLInputElement>
                        ) => {
                          setNewValue(event?.target.value);
                        }}
                        defaultValue={elem.value}
                        className="edit__popup"
                        modalRef={modalRef}
                      />
                    ))}
                </td>
                <td style={{ minWidth: 200 }}>
                  <Select
                    value={{
                      value: elem.hotline || "",
                      label: elem.hotline || " ",
                    }}
                    options={[
                      { value: "", label: "\u00A0" },
                      ...(report?.cvHotlines?.map((h: string) => ({
                        value: h,
                        label: h,
                      })) || []),
                    ]}
                    name="cvHotlines"
                    propsChange={(selectedOption: {
                      value: string;
                      label: string;
                    }) => {
                      handleHotlineSelectChange(selectedOption, elem);
                    }}
                    style={{ width: "100%" }}
                    isHotlineChanged={
                      elem?.changes &&
                      formatReportData(elem?.changes)?.includes("hotline")
                    }
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default SkuCvTableSection;
